import { cn } from '@tempo-io/sui-class-utils';
import { CircleFilled } from '@tempo-io/sui-icons';
import { Select } from '@tempo-io/sui-select';
import { Colors, PickerColors } from '@/common/utils/colors';

type ColorOption = {
  value: string;
  label: string;
  fill: string;
};

type ColorData = {
  name: string;
  hex: string;
  fill: string;
};

const alphaSortedColorByName = [...PickerColors].sort((a, b) => a.name.localeCompare(b.name));

const createColorOptions = ({ colors }: { colors: ColorData[] }): ColorOption[] => {
  return colors.map((color) => ({
    value: color.hex,
    label: color.name,
    fill: color.fill,
  }));
};

const findSelectedColor = ({
  value,
  colors,
}: {
  value: string;
  colors: ColorData[];
}): ColorOption | null => {
  const foundColor = colors.find((color) => color.hex === value);
  return foundColor
    ? { value: foundColor.hex, label: foundColor.name, fill: foundColor.fill }
    : null;
};

const ColorOption = ({ option }: { option: ColorOption }) => (
  <div className="flex items-center gap-2">
    <CircleFilled className={cn(option.fill, 'h-4 w-4')} />
    <span data-testid="displayedColorName">{option.label}</span>
  </div>
);

const colorOptions = createColorOptions({ colors: alphaSortedColorByName });
export const defaultColorPickerHex = Colors.amber.hex;

export const ColorPicker = ({
  name,
  value,
  onChange,
}: {
  name: string | undefined;
  value: string;
  onChange: (color: string) => void;
}) => {
  const selectedColor = findSelectedColor({ value, colors: alphaSortedColorByName });

  const handleColorChange = (selectedOption: ColorOption | null) => {
    if (selectedOption) {
      onChange(selectedOption.value);
    }
  };

  return (
    <Select
      menuPosition="fixed"
      name={name}
      className="w-42"
      value={selectedColor}
      onChange={handleColorChange}
      options={colorOptions}
      formatOptionLabel={(option: ColorOption) => <ColorOption option={option} />}
      isClearable={false}
      data-testid="colorTrigger"
    />
  );
};
